import { Fragment } from 'react';
import { Box, Button, ButtonBase, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import type { NotificationItem } from './api';
import {
  groupNotifications,
  isUnread,
  isUpdated,
  kindOf,
  plain,
  progressText,
  stateToneOf,
  whenText,
} from './format';

/** Small chips are painted grey by a page-wide rule: colours are forced (as AccessHistoryPage). */
const chipSx = (fg: string, bg: string) => ({
  height: 22,
  fontSize: '11.5px',
  fontWeight: 600,
  color: `${fg} !important`,
  backgroundColor: `${bg} !important`,
  border: 'none !important',
  '& .MuiChip-label': { color: `${fg} !important`, px: 1 },
});

/** The icon of a finished item: a tick when done, an error mark when failed (decorative: the
 * state's word stands beside it). */
export function StateIcon({ item, testId }: { item: NotificationItem; testId: string }) {
  const tone = stateToneOf(item);
  if (!tone || item.state === 'in_progress') return null;
  const Icon = item.state === 'failed' ? ErrorOutlineIcon : CheckCircleOutlineIcon;
  return (
    <Icon
      aria-hidden
      data-testid={`${testId}-state-icon`}
      data-icon={item.state}
      sx={{ fontSize: 16, color: tone.fg, flex: 'none' }}
    />
  );
}

/** The bar of an item in progress: the percentage, or indeterminate without one. */
export function ProgressLine({ item, testId }: { item: NotificationItem; testId: string }) {
  if (item.state !== 'in_progress') return null;
  const known = item.progress !== null;
  return (
    <Stack
      component='span'
      direction='row'
      spacing={1}
      alignItems='center'
      sx={{ width: '100%', maxWidth: 320 }}
      data-testid={`${testId}-progress`}
    >
      <LinearProgress
        variant={known ? 'determinate' : 'indeterminate'}
        value={known ? (item.progress as number) : undefined}
        color='secondary'
        aria-label={progressText(item.progress)}
        sx={{ flex: 1, height: 6, borderRadius: 3 }}
      />
      <Typography
        component='span'
        sx={{
          fontSize: '12px',
          fontWeight: 600,
          color: '#1565c0',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {progressText(item.progress)}
      </Typography>
    </Stack>
  );
}

/** The row's tone by state: a coloured left edge for done / failed / in progress. */
const toneEdge = (item: NotificationItem) => {
  const tone = stateToneOf(item);
  return tone ? { boxShadow: `inset 3px 0 0 ${tone.fg}` } : {};
};

function UnreadDot() {
  return (
    <Box
      component='span'
      aria-label='unread'
      role='img'
      sx={{
        ml: 'auto',
        width: 8,
        height: 8,
        borderRadius: '4px',
        bgcolor: 'secondary.main',
        flex: 'none',
      }}
    />
  );
}

/** The chip, the state's mark, the time ("updated ..." once updated) and the unread dot. */
function RowHead({ item, now, testId }: { item: NotificationItem; now?: Date; testId: string }) {
  const kind = kindOf(item);
  const tone = stateToneOf(item);
  const when = [
    whenText(item.created_at, now),
    isUpdated(item) && item.updated_at ? `updated ${whenText(item.updated_at, now)}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Stack direction='row' spacing={1.25} alignItems='center' sx={{ width: '100%', minWidth: 0 }}>
      <Chip size='small' label={kind.label} sx={chipSx(kind.fg, kind.bg)} />
      {tone && item.state !== 'in_progress' && (
        <Stack direction='row' spacing={0.5} alignItems='center' sx={{ flex: 'none' }}>
          <StateIcon item={item} testId={testId} />
          <Typography
            component='span'
            sx={{ fontSize: '12px', fontWeight: 600, color: tone.fg }}
            data-testid={`${testId}-state`}
          >
            {tone.label}
          </Typography>
        </Stack>
      )}
      {when && (
        <Typography
          variant='caption'
          color='text.secondary'
          noWrap
          sx={{ minWidth: 0, fontSize: '12px' }}
        >
          {when}
        </Typography>
      )}
      {isUnread(item) && <UnreadDot />}
    </Stack>
  );
}

type RowProps = {
  item: NotificationItem;
  // eslint-disable-next-line no-unused-vars
  onOpen: (item: NotificationItem) => void;
  testId: string;
  now?: Date;
};

/** An item that waits for the person: its button opens what it is about. */
function ActionRow({ item, onOpen, testId, now }: RowProps) {
  const unread = isUnread(item);
  const label = plain(item.action?.label);
  return (
    <Stack
      spacing={0.75}
      data-testid={testId}
      data-unread={unread ? 'true' : 'false'}
      data-state={item.state}
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: '1px solid #f3e7cf',
        bgcolor: item.state === 'failed' ? '#fff6f5' : unread ? '#fffaf0' : '#fffdf8',
        ...toneEdge(item),
      }}
    >
      <RowHead item={item} now={now} testId={testId} />
      <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.35 }}>
        {plain(item.title)}
      </Typography>
      {plain(item.summary) && (
        <Typography sx={{ fontSize: '12.5px' }} color='text.secondary'>
          {plain(item.summary)}
        </Typography>
      )}
      <ProgressLine item={item} testId={testId} />
      <Box>
        <Button
          variant='contained'
          color='secondary'
          size='small'
          onClick={() => onOpen(item)}
          data-testid={`${testId}-action`}
        >
          {label || 'Open'}
        </Button>
      </Box>
    </Stack>
  );
}

/** Any other item: the whole row opens it. */
function PlainRow({ item, onOpen, testId, now }: RowProps) {
  const unread = isUnread(item);
  return (
    <ButtonBase
      onClick={() => onOpen(item)}
      data-testid={testId}
      data-unread={unread ? 'true' : 'false'}
      data-state={item.state}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 0.5,
        width: '100%',
        px: 2,
        py: 1.5,
        textAlign: 'left',
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: item.state === 'failed' ? '#fff6f5' : unread ? '#f5f9ff' : 'background.paper',
        '&:hover': { bgcolor: unread ? '#eaf2ff' : '#f7f8fa' },
        ...toneEdge(item),
      }}
    >
      <RowHead item={item} now={now} testId={testId} />
      <Typography
        component='span'
        sx={{ fontSize: '14px', fontWeight: unread ? 600 : 400, lineHeight: 1.35 }}
      >
        {plain(item.title)}
      </Typography>
      {plain(item.summary) && (
        <Typography component='span' sx={{ fontSize: '12.5px' }} color='text.secondary'>
          {plain(item.summary)}
        </Typography>
      )}
      <ProgressLine item={item} testId={testId} />
    </ButtonBase>
  );
}

function SectionLabel({ children, action = false }: { children: string; action?: boolean }) {
  return (
    <Typography
      sx={{
        px: 2,
        pt: 1.5,
        pb: 0.5,
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: action ? '#8a4b00' : 'text.secondary',
      }}
    >
      {children}
    </Typography>
  );
}

/**
 * The compact list of the bell's panel (mock-up "Notifications"): "Needs your action" first,
 * each with its button, then Today / Yesterday / Earlier. Titles and summaries are shown as
 * the server wrote them, as text. Ids are never shown (test ids use the position).
 */
export function NotificationGroups({
  items,
  onOpen,
  testId = 'notification',
  now,
}: {
  items: NotificationItem[];
  // eslint-disable-next-line no-unused-vars
  onOpen: (item: NotificationItem) => void;
  testId?: string;
  now?: Date;
}) {
  const { action, days } = groupNotifications(items, now);
  let n = 0;
  return (
    <Box>
      {action.length > 0 && (
        <>
          <SectionLabel action>Needs your action</SectionLabel>
          {action.map((item) => (
            <ActionRow
              key={`action-${item.id}`}
              item={item}
              onOpen={onOpen}
              testId={`${testId}-${n++}`}
              now={now}
            />
          ))}
        </>
      )}
      {days.map((day) => (
        <Fragment key={day.label}>
          <SectionLabel>{day.label}</SectionLabel>
          {day.items.map((item) => (
            <PlainRow
              key={`row-${item.id}`}
              item={item}
              onOpen={onOpen}
              testId={`${testId}-${n++}`}
              now={now}
            />
          ))}
        </Fragment>
      ))}
    </Box>
  );
}

/** "Nothing here. You are up to date." */
export function NothingHere() {
  return (
    <Box
      data-testid='notifications-empty'
      sx={{ px: 2, py: 4, textAlign: 'center', color: 'text.secondary', fontSize: '14px' }}
    >
      Nothing here. You are up to date.
    </Box>
  );
}
